import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Divider,
  IconButton,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Grid2,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  AssignmentReturn as AssignmentReturnIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import CustomTextField from "../../../components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { SwageazyInventoryProductRequestVariant } from "../../../../models/SwageazyInventory/SwageazyInventory";
import { useNotification } from "../../../../utils/NotificationsContext";
import AppButton from "../../../components/common/buttons/AppButton";
import { SwageazyInventorySampleReturnRequestFormData } from "../../../../models/SwageazyInventory/SwageazyInventory";
import { Warehouse } from "../../../../models/Warehouse/Warehouse";

interface SwageazyInventorySampleReturnProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const SwageazyInventorySampleReturn: React.FC<
  SwageazyInventorySampleReturnProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { warehouseStore, swageazyInventoryStore, orderStore, productStore } =
    useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (open && !swageazyInventoryStore.sampleReturnRequestFormData) {
      swageazyInventoryStore.setSampleReturnRequestFormData({
        variants: [],
      });

      handleWarehouseSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetSampleReturnRequestFormData();
      swageazyInventoryStore.setAvailableVariantOptions({});
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      swageazyInventoryStore.clearApiTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrderSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        orderStore
          .searchOrderIds(searchText)
          .then(() => {
            const orderIds = orderStore.orderIds || [];
            const searchResults = orderIds.map((orderId) => ({
              id: String(orderId),
              value: String(orderId),
            }));
            swageazyInventoryStore.setOrderSearchResults(searchResults);
          })
          .catch((err: any) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          });
      }, 800);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setOrderSearchResults([]);
    }
  };

  const handleOrderSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const orderId = Number(selected[0]?.id);
      if (!isNaN(orderId) && orderId > 0) {
        swageazyInventoryStore.setAvailableVariantOptions({});
        swageazyInventoryStore.setSampleReturnRequestFormData({
          order_id: orderId,
          product_id: undefined,
          variants: [],
          city_id: undefined,
          warehouse_id: undefined,
          current_rack: undefined,
          box_id: undefined,
        });

        orderStore
          .getOrderDetails(orderId)
          .then(() => {
            if (
              orderStore.existingOrder &&
              orderStore.existingOrder?.products &&
              orderStore.existingOrder?.products?.length > 0
            ) {
              const products =
                orderStore.existingOrder?.products?.map((orderProduct) => ({
                  id: orderProduct?.id,
                  product_id: orderProduct?.id,
                  name: orderProduct?.name || orderProduct?.label || "",
                  description: orderProduct?.desc || null,
                  quantity: orderProduct?.quantity || 0,
                  sku: orderProduct?.sku || "",
                  rate: orderProduct?.cost_price || 0,
                  vendor_code: "",
                  selling_price: orderProduct?.selling_price || 0,
                  order_selling_price: orderProduct?.selling_price || 0,
                  gst_percentage: orderProduct?.gst_percentage || 0,
                  hsn_code: orderProduct?.hsn_code || "",
                  pr_id: 0,
                  variants: [],
                  has_expiry: undefined,
                })) || [];
              swageazyInventoryStore.setOrderProducts(products);
            } else {
              swageazyInventoryStore.setOrderProducts([]);
            }
          })
          .catch((err: any) => {
            swageazyInventoryStore.setOrderProducts([]);
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to fetch order details. Please try again.",
            );
          });
      }
    } else {
      swageazyInventoryStore.setAvailableVariantOptions({});
      swageazyInventoryStore.setSampleReturnRequestFormData({
        order_id: undefined,
        product_id: undefined,
        variants: [],
        city_id: undefined,
        warehouse_id: undefined,
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const handleProductSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const selectedProductId = Number(selected[0]?.id);

      const selectedProduct = swageazyInventoryStore.orderProducts?.find(
        (product) => product?.id === selectedProductId,
      );

      if (selectedProduct) {
        const productIdForApi =
          selectedProduct?.product_id || selectedProductId;

        swageazyInventoryStore.setSampleReturnRequestFormData({
          product_id: productIdForApi,
          variants: [],
        });
        swageazyInventoryStore.setAvailableVariantOptions({});

        productStore
          .getProductDetailsById(productIdForApi)
          .then(() => {
            const variants = productStore.productDetails?.variants || [];

            if (variants?.length > 0) {
              const optionsMap: {
                [key: string]: FilterValue[];
              } = {};

              variants.forEach((variant) => {
                if (variant?.options_name) {
                  const optionName = variant.options_name;
                  const optionValue = variant?.title || variant?.value || "";

                  if (!optionsMap[optionName]) {
                    optionsMap[optionName] = [];
                  }

                  const exists = optionsMap[optionName]?.some(
                    (item) => item?.value === optionValue,
                  );

                  if (!exists && optionValue) {
                    optionsMap[optionName]?.push({
                      id: String(variant?.id || `${optionName}-${optionValue}`),
                      value: optionValue,
                    });
                  }
                }
              });

              swageazyInventoryStore.setAvailableVariantOptions(optionsMap);

              const initialVariant: SwageazyInventoryProductRequestVariant = {
                sku: `variant-${Date.now()}`,
                quantity: undefined,
                quantity_rejected: undefined,
                rejection_reason: undefined,
                variant_options: {},
              };
              swageazyInventoryStore.setSampleReturnRequestFormData({
                variants: [initialVariant],
              });
            } else {
              swageazyInventoryStore.setAvailableVariantOptions({});
              swageazyInventoryStore.setSampleReturnRequestFormData({
                variants: [],
              });
            }
          })
          .catch((err: any) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to fetch product details. Please try again.",
            );
            swageazyInventoryStore.setAvailableVariantOptions({});
            swageazyInventoryStore.setSampleReturnRequestFormData({
              variants: [],
            });
          });
      }
    } else {
      swageazyInventoryStore.setSampleReturnRequestFormData({
        product_id: undefined,
        variants: [],
      });
      swageazyInventoryStore.setAvailableVariantOptions({});
    }
  };

  const addVariant = () => {
    const newVariant: SwageazyInventoryProductRequestVariant = {
      sku: `variant-${Date.now()}`,
      quantity: undefined,
      quantity_rejected: undefined,
      rejection_reason: undefined,
      variant_options: {},
    };
    swageazyInventoryStore.setSampleReturnRequestFormData({
      variants: [
        ...(swageazyInventoryStore.sampleReturnRequestFormData?.variants || []),
        newVariant,
      ],
    });
  };

  const removeVariant = (id: string) => {
    swageazyInventoryStore.setSampleReturnRequestFormData({
      variants:
        (
          swageazyInventoryStore.sampleReturnRequestFormData?.variants || []
        ).filter((v) => (v?.sku || "") !== id) || [],
    });
  };

  const updateVariant = (
    id: string,
    field: keyof SwageazyInventoryProductRequestVariant,
    value: string | number | undefined,
  ) => {
    swageazyInventoryStore.setSampleReturnRequestFormData({
      variants:
        swageazyInventoryStore.sampleReturnRequestFormData?.variants?.map(
          (v) => {
            if ((v?.sku || "") === id) {
              if (field === "variant_options") {
                return {
                  ...v,
                  variant_options: value || {},
                };
              }
              return { ...v, [field]: value };
            }
            return v;
          },
        ) || [],
    });
  };

  const updateVariantOption = (
    variantId: string,
    optionName: string,
    selectedValue: string,
  ) => {
    swageazyInventoryStore.setSampleReturnRequestFormData({
      variants:
        swageazyInventoryStore.sampleReturnRequestFormData?.variants?.map(
          (v) => {
            if ((v?.sku || "") === variantId) {
              return {
                ...v,
                variant_options: {
                  ...(v?.variant_options || {}),
                  [optionName]: selectedValue,
                },
              };
            }
            return v;
          },
        ) || [],
    });
  };

  // const handleCitySearch = (searchText?: string) => {
  //   swageazyInventoryStore.clearApiTimer();
  //   if (searchText && searchText.length > 2) {
  //     const timer = setTimeout(() => {
  //       addressStore
  //         .getCity({
  //           page_no: 1,
  //           search_text: searchText,
  //         })
  //         .catch(() => {});
  //     }, 800);
  //     swageazyInventoryStore.setApiTimer(timer);
  //   }
  // };

  const handleWarehouseSearch = () => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch(() => {});
    }
  };

  const handleWarehouseRackSearch = (
    warehouseId: string | number | undefined,
  ) => {
    if (warehouseId) {
      warehouseStore
        .searchWarehouseRack(String(warehouseId), "")
        .then(() => {
          if (warehouseStore.warehouseRacks?.length === 1) {
            const autoSelectedRackId =
              warehouseStore.warehouseRacks[0]?.id || "";
            swageazyInventoryStore.setSampleReturnRequestFormData({
              current_rack: autoSelectedRackId,
              box_id: undefined,
            });
          } else {
            swageazyInventoryStore.setSampleReturnRequestFormData({
              current_rack: undefined,
              box_id: undefined,
            });
          }
        })
        .catch(() => {
          swageazyInventoryStore.setSampleReturnRequestFormData({
            current_rack: undefined,
            box_id: undefined,
          });
        });
    } else {
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setSampleReturnRequestFormData({
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const canSubmit = () => {
    const formData = swageazyInventoryStore.sampleReturnRequestFormData;

    const hasAllRequiredFields =
      !!formData?.order_id &&
      !!formData?.product_id &&
      !!formData?.warehouse_name &&
      !!formData?.current_rack &&
      !!formData?.box_id;

    if (!hasAllRequiredFields) {
      return false;
    }

    const variants = formData?.variants || [];
    if (variants.length === 0) {
      return false;
    }

    const availableOptions = swageazyInventoryStore.availableVariantOptions;
    const requiredOptionKeys = Object.keys(availableOptions);

    const allVariantsComplete = variants.every((variant) => {
      if (!variant.quantity || variant.quantity <= 0) {
        return false;
      }

      if (requiredOptionKeys.length > 0) {
        const variantOptions = variant.variant_options || {};
        const allOptionsFilled = requiredOptionKeys.every((optionKey) => {
          const optionValue = variantOptions[optionKey];
          return (
            optionValue !== undefined &&
            optionValue !== null &&
            String(optionValue).trim() !== ""
          );
        });
        return allOptionsFilled;
      }

      return true;
    });

    return allVariantsComplete;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "30vh", sm: "28vh" },
            maxHeight: { xs: "90vh", sm: "85vh", md: "75vh", lg: "70vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "35vh", sm: "32vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            {/* <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.customColors.greyTextColor,
                marginBottom: 4,
              }}
            >
              Order: Swageazy Inventory
            </CustomTypography> */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentReturnIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                Sample Returned
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Order ID
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Select an order from the list of orders.
              </CustomTypography>
              <AppSearchDropDown2
                title="Order ID"
                isMultiSelect={false}
                list={swageazyInventoryStore.orderSearchResults}
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData?.order_id
                    ? [
                        {
                          id: String(
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.order_id,
                          ),
                          value: String(
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.order_id,
                          ),
                        },
                      ]
                    : []
                }
                handleSelect={handleOrderSelect}
                handleSearch={handleOrderSearch}
                local={false}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Product
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Select the product from the selected Order.
              </CustomTypography>
              <AppSearchDropDown2
                title="Products"
                isMultiSelect={false}
                list={
                  swageazyInventoryStore.orderProducts?.map((product) => ({
                    id: String(product?.id),
                    value: String(product?.name),
                  })) || []
                }
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData?.product_id
                    ? [
                        {
                          id: String(
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.product_id,
                          ),
                          value:
                            swageazyInventoryStore.orderProducts?.find(
                              (p) =>
                                p?.id ===
                                swageazyInventoryStore
                                  .sampleReturnRequestFormData?.product_id,
                            )?.name || "",
                        },
                      ]
                    : []
                }
                handleSelect={handleProductSelect}
                local={true}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>
          </Grid2>

          {swageazyInventoryStore.sampleReturnRequestFormData?.product_id && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <CustomTypography variant="heading6" fontWeight={600}>
                  Product Variants{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>

                <AppButton
                  onClick={addVariant}
                  variant="primary"
                  label="Add Variant"
                  customStyles={{ height: 20 }}
                  disabled={
                    !swageazyInventoryStore.sampleReturnRequestFormData
                      ?.product_id
                  }
                />
              </Box>

              {Object.keys(swageazyInventoryStore.availableVariantOptions || {})
                .length === 0 &&
              swageazyInventoryStore.sampleReturnRequestFormData?.product_id ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    color: theme.palette.customColors.greyTextColor,
                    border: `1px dashed ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <CustomTypography variant="body1">
                    No variant found
                  </CustomTypography>
                </Box>
              ) : (
                swageazyInventoryStore.sampleReturnRequestFormData?.variants?.map(
                  (
                    variant: SwageazyInventoryProductRequestVariant,
                    index: number,
                  ) => (
                    <Box
                      key={variant?.sku || `variant-${index}`}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: `1px solid ${theme.palette.customColors.greyBorder}`,
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Dynamically render variant option fields */}
                        {Object.keys(
                          swageazyInventoryStore.availableVariantOptions || {},
                        ).map((optionName) => (
                          <Box key={optionName} sx={{ flex: 1, minWidth: 200 }}>
                            <AppSearchDropDown2
                              title={`Select ${optionName}`}
                              list={
                                swageazyInventoryStore
                                  .availableVariantOptions?.[optionName] || []
                              }
                              value={
                                variant?.variant_options?.[optionName]
                                  ? [
                                      {
                                        id: String(
                                          variant?.variant_options?.[
                                            optionName
                                          ],
                                        ),
                                        value: String(
                                          variant?.variant_options?.[
                                            optionName
                                          ],
                                        ),
                                      },
                                    ]
                                  : []
                              }
                              handleSelect={(selected) =>
                                updateVariantOption(
                                  variant?.sku || `variant-${index}`,
                                  optionName,
                                  selected?.length > 0
                                    ? selected[0]?.value
                                    : "",
                                )
                              }
                              local={true}
                              minWidth="100%"
                              borderRadius="8px"
                            />
                          </Box>
                        ))}
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                          <CustomTextField
                            width="100%"
                            label="Quantity"
                            placeholder="Quantity"
                            value={variant?.quantity || ""}
                            onChange={(e) =>
                              updateVariant(
                                String(variant?.sku),
                                "quantity",
                                Number(e.target.value) || undefined,
                              )
                            }
                            type="number"
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            minWidth: 60,
                          }}
                        >
                          <IconButton
                            onClick={() =>
                              removeVariant(variant?.sku || `variant-${index}`)
                            }
                            sx={{
                              color: theme.palette.customColors.lightred,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightred + "10",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ),
                )
              )}
            </Box>
          )}

          <Divider sx={{ mb: 1 }} />
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Warehouse
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={false}
                title={"Select Warehouse"}
                local={true}
                backgroundColor={theme.palette.customColors.grayBackGroundColor}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="13.938rem"
                list={
                  warehouseStore.warehouses?.map((item) => {
                    return { id: String(item?.id), value: String(item?.id) };
                  }) || []
                }
                value={
                  warehouseStore.warehouses?.length > 0 &&
                  (swageazyInventoryStore.sampleReturnRequestFormData
                    ?.warehouse_id ||
                    swageazyInventoryStore.sampleReturnRequestFormData
                      ?.warehouse_name)
                    ? (() => {
                        const warehouseId =
                          swageazyInventoryStore.sampleReturnRequestFormData
                            ?.warehouse_id;
                        const warehouseName =
                          swageazyInventoryStore.sampleReturnRequestFormData
                            ?.warehouse_name;

                        let warehouse: Warehouse | undefined = undefined;

                        if (warehouseId !== undefined && warehouseId !== null) {
                          warehouse =
                            warehouseStore.warehouses?.find(
                              (w) => String(w?.id) === String(warehouseId),
                            ) || undefined;
                        }

                        if (!warehouse && warehouseName) {
                          warehouse = warehouseStore.warehouses?.find(
                            (w) =>
                              String(w?.id) === warehouseName ||
                              w?.city_sku === warehouseName,
                          );
                        }

                        return warehouse
                          ? [
                              {
                                id: String(warehouse?.id),
                                value: String(warehouse?.id),
                              },
                            ]
                          : [];
                      })()
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem && selectedItem.length > 0) {
                    const warehouseUpdate: SwageazyInventorySampleReturnRequestFormData =
                      {};

                    warehouseUpdate.warehouse_id = selectedItem[0].id;
                    warehouseUpdate.warehouse_name = selectedItem[0].value;

                    swageazyInventoryStore.setSampleReturnRequestFormData(
                      warehouseUpdate,
                    );

                    handleWarehouseRackSearch(selectedItem[0].id);
                  } else {
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      warehouse_id: undefined,
                      warehouse_name: undefined,
                    });
                    handleWarehouseRackSearch(undefined);
                  }
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Rack
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                title="Select Rack"
                list={
                  warehouseStore.warehouseRacks?.map((item) => {
                    return {
                      id: String(item?.id),
                      value: String(item?.id),
                    };
                  }) || []
                }
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData
                    ?.current_rack
                    ? [
                        {
                          id: swageazyInventoryStore.sampleReturnRequestFormData
                            ?.current_rack,
                          value:
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.current_rack,
                        },
                      ]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem?.length > 0) {
                    const selectedRackId = selectedItem[0]?.id || "";
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      current_rack: selectedRackId,
                      box_id: undefined,
                    });
                  } else {
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      current_rack: undefined,
                      box_id: undefined,
                    });
                  }
                }}
                local={true}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Box
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTextField
                width="100%"
                placeholder="Enter Box ID"
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData?.box_id ||
                  ""
                }
                onChange={(e) => {
                  swageazyInventoryStore.setSampleReturnRequestFormData({
                    box_id: e.target.value,
                  });
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={onSave}
            variant="primary"
            label="Save"
            disabled={!canSubmit()}
            customStyles={{
              backgroundColor: canSubmit()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: canSubmit()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: canSubmit() ? "translateY(-1px)" : "none",
                boxShadow: canSubmit()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(SwageazyInventorySampleReturn);
